import { computed, ref } from '@nuxtjs/composition-api';
import { fetchRecurlySellable } from '@/services/PricePlans';
import {
  SUPERVISION_LEVEL_PRODUCT_KEYS,
  SUPERVISION_PRODUCT_KEYS,
} from '@/constants/Experiments';
import useRegistration, { RegistrationStateNames } from './useRegistration';

/**
 * The sellable plan a declared company size prices at, out of one product's ladder. Payment
 * converts on this band and the plan screen prices it, so the two always agree (BIO-2158 review).
 *
 * Registration asks size in the ladder's own six bands (BIO-2201), which match exactly. An older
 * lead may hold a Dedicated range: one inside a band prices there (1-4 and 5-19 at 1-19), one
 * straddling two (71-149, 150-249, 250-500) at the band of its low end, and no size at all at
 * the first band (0-19). The customer can change plan or band later.
 */
export const bandPlanFor = (plans = [], declaredRange = '') => {
  const [min, max] = String(declaredRange || '1-1')
    .split('-')
    .map(Number);
  const active = plans.filter((plan) => plan.status === 'active');

  return (
    active.find((plan) => plan.minSize <= min && max <= plan.maxSize) ||
    active.find((plan) => plan.minSize <= min && min <= plan.maxSize)
  );
};

const sellable = ref([]);
const isLoading = ref(false);
const error = ref(null);

const load = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    sellable.value = await fetchRecurlySellable(SUPERVISION_PRODUCT_KEYS);
  } catch (e) {
    error.value = e;
  } finally {
    isLoading.value = false;
  }
};

/**
 * The two plans of the level the customer picked, priced at their band from the Recurly
 * sellable list (the same list convert and changePlan bill from). `plans` is in ladder
 * order, cheaper first; `isComplete` is false when either plan is missing at the band.
 */
const useSupervisionPlans = () => {
  const { state } = useRegistration();

  if (!sellable.value.length && !isLoading.value) {
    load();
  }

  const level = computed(() => state[RegistrationStateNames.SUPERVISION_LEVEL]);
  const productKeys = computed(
    () => SUPERVISION_LEVEL_PRODUCT_KEYS[level.value] || []
  );
  const plans = computed(() =>
    productKeys.value
      .map((key) =>
        bandPlanFor(
          sellable.value.filter((plan) => plan.productKey === key),
          state[RegistrationStateNames.NUM_EMPLOYEES]
        )
      )
      .filter(Boolean)
  );
  const band = computed(() => plans.value[0]?.employeeSizeMapping || '');
  const isComplete = computed(
    () =>
      !isLoading.value &&
      !error.value &&
      plans.value.length === productKeys.value.length
  );

  return { level, plans, band, isLoading, error, isComplete, reload: load };
};

export default useSupervisionPlans;
